import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Sentry } from '@/lib/sentry';
import posthog from 'posthog-js';

interface AuthErrorShape {
  message: string;
  code?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, next?: string | null, turnstileToken?: string | null) => Promise<{ error: AuthErrorShape | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthErrorShape | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getEmailDomain = (email?: string | null): string | undefined => {
  const domain = email?.split('@')[1];
  return domain || undefined;
};

const identifyPostHogUser = (
  authUser: Pick<User, 'id' | 'email'> | null | undefined,
  fallbackEmail?: string,
) => {
  if (!authUser?.id) return;
  posthog.identify(authUser.id, {
    email_domain: getEmailDomain(authUser.email ?? fallbackEmail),
  });
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Identify the user to Sentry by id only — never email — so we can
        // correlate errors without leaking PII into the issue tracker.
        if (session?.user?.id) {
          Sentry.setUser({ id: session.user.id });
          identifyPostHogUser(session.user);
        } else {
          Sentry.setUser(null);
        }

        if (event === 'SIGNED_OUT') {
          posthog.reset();
        }

        // The welcome email is now step 1 of the post-signup sequence,
        // enrolled in signup-with-ip and dispatched by send-sequence-emails
        // on the pg_cron tick. The old send-welcome-email function is kept
        // for rollback (see its file header) but is no longer invoked here.
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // If we have a local session but Supabase no longer recognizes it,
      // clear it so the app redirects to /auth (prevents "Auth session missing" loops).
      if (session) {
        const { error: userError } = await supabase.auth.getUser();
        if (userError) {
          await supabase.auth.signOut({ scope: 'local' });
          setSession(null);
          setUser(null);
          posthog.reset();
        } else {
          identifyPostHogUser(session.user);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, next?: string | null, turnstileToken?: string | null) => {
    try {
      // Use the edge function for IP tracking
      const { data, error } = await supabase.functions.invoke('signup-with-ip', {
        body: {
          email,
          password,
          ...(next ? { next } : {}),
          ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
        }
      });

      if (error) {
        // Try to parse error message for structured error from edge function
        // Error format: "Edge function returned 400: Error, {"error":{"code":"...","message":"..."}}"
        const errorMessage = error.message || '';
        const jsonStartIndex = errorMessage.indexOf('{');
        if (jsonStartIndex !== -1) {
          try {
            const jsonString = errorMessage.slice(jsonStartIndex);
            const parsedError = JSON.parse(jsonString);
            if (parsedError.error) {
              return { error: parsedError.error };
            }
          } catch {
            // If parsing fails, return a generic error
          }
        }
        return { error: { message: 'An error occurred during signup. Please try again.' } };
      }

      if (data?.error) {
        return { error: data.error };
      }

      identifyPostHogUser(data?.user, email);
      posthog.capture('signup_completed');

      return { error: null };
    } catch (error) {
      return { error: { message: 'An unexpected error occurred during signup' } };
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (!error) {
      identifyPostHogUser(data.user, email);
      posthog.capture('signin_completed');
    }
    return { error };
  };

  const signOut = async () => {
    // Use local scope to clear session even if server session is already gone
    await supabase.auth.signOut({ scope: 'local' });
    setSession(null);
    setUser(null);
    Sentry.setUser(null);
    posthog.reset();
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
